<template>
  <div>
    <div style="overflow-y: auto; overflow-x: hidden">
      <div class="admin-table-container">
        <table
          class="admin-table"
          style="width: 100%; table-layout: fixed !important"
        >
          <colgroup>
            <col
              v-for="(header, index) in headers"
              :key="index"
              :style="{ width: 'auto' }"
            />
            <col
              v-if="isAction || customActions.length > 0"
              style="width: 10% !important"
            />
          </colgroup>
          <thead>
            <tr>
              <th
                v-for="(header, headerIndex) in headers"
                :key="header.text"
                :class="{
                  'rounded-header-first':
                    variant === 'rounded' && headerIndex === 0,
                  'rounded-header-last':
                    variant === 'rounded' &&
                    headerIndex === headers.length - 1 &&
                    !(isAction || customActions.length > 0),
                }"
                :style="getHeaderStyle(headerIndex)"
                @click="header.sortable ? sortColumn(header.value) : null"
              >
                <div
                  :style="{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    justifyContent:
                      variant === 'rounded' || isHeaderCentered(header)
                        ? 'center'
                        : 'flex-start',
                  }"
                >
                  <span :style="getHeaderTextStyle(header)">
                    {{ header.text }}
                  </span>
                  <SortIcon v-if="header.sortable" />
                  <div
                    v-if="header.info"
                    class="info-icon-container"
                    @click.stop
                  >
                    <img
                      src="/images/icon/info-circle.svg"
                      alt="info"
                      class="info-icon"
                    />
                    <div class="tooltip">
                      <div v-html="header.info"></div>
                    </div>
                  </div>
                </div>
              </th>
              <th
                v-if="isAction || customActions.length > 0"
                class="action-column-header"
                :class="{
                  'rounded-header-last': variant === 'rounded',
                }"
                :style="getActionHeaderStyle()"
              >
                Action
              </th>
            </tr>
          </thead>

          <tbody>
            <!-- Skeleton Loading -->
            <template v-if="loading">
              <tr style="position: relative">
                <td
                  :colspan="headers.length"
                  style="padding: 10px; border: none"
                >
                  <div class="spinner-container">
                    <div class="spinner"></div>
                  </div>
                </td>
                <td
                  v-if="isAction || customActions.length > 0"
                  style="width: 10% !important; padding: 0; border: none"
                >
                  <!-- Empty cell to maintain column width during loading -->
                </td>
              </tr>
            </template>

            <!-- Actual Data -->
            <template v-else>
              <tr v-if="!items?.length" style="position: relative">
                <td
                  :colspan="headers.length"
                  style="
                    text-align: center;
                    padding: 20px;
                    font-size: var(--fs-medium);
                  "
                >
                  No data available
                </td>
                <td
                  v-if="isAction || customActions.length > 0"
                  style="width: 10% !important; padding: 0; border: none"
                >
                  <!-- Empty cell to maintain column width when no data -->
                </td>
              </tr>
              <tr
                v-else
                v-for="(item, itemIndex) in sortedItems"
                :key="item.id"
              >
                <td
                  v-for="(header, headerIndex) in headers"
                  :key="header.text"
                  :class="{
                    'rounded-cell-first-last-row':
                      variant === 'rounded' &&
                      headerIndex === 0 &&
                      itemIndex === sortedItems.length - 1,
                    'rounded-cell-last-last-row':
                      variant === 'rounded' &&
                      headerIndex === headers.length - 1 &&
                      itemIndex === sortedItems.length - 1 &&
                      !(isAction || customActions.length > 0),
                  }"
                  :style="getCellStyle(headerIndex)"
                >
                  <template v-if="$slots[`cell-${header.value}`]">
                    <slot
                      :name="`cell-${header.value}`"
                      :item="item"
                      :value="item[header.value]"
                      :header="header"
                    />
                  </template>
                  <template v-else>
                    {{
                      typeof item[header.value] === "string"
                        ? item[header.value].trim() || "-"
                        : item[header.value]
                    }}
                  </template>
                </td>

                <td
                  v-if="isAction || customActions.length > 0"
                  class="action-column-cell"
                  :class="{
                    'rounded-cell-last-last-row':
                      variant === 'rounded' &&
                      itemIndex === sortedItems.length - 1,
                  }"
                  :style="getActionCellStyle()"
                >
                  <div class="action-menu-wrapper">
                    <v-menu
                      :model-value="
                        activeActionMenu === getActionMenuKey(item, itemIndex)
                      "
                      location="bottom end"
                      offset="8"
                      :close-on-content-click="true"
                      content-class="table-action-menu-content"
                      @update:model-value="
                        setActiveActionMenu($event, item, itemIndex)
                      "
                    >
                      <template #activator="{ props: menuProps }">
                        <button
                          v-bind="menuProps"
                          type="button"
                          class="table-action-trigger"
                          :class="{
                            'table-action-trigger--active':
                              activeActionMenu ===
                              getActionMenuKey(item, itemIndex),
                          }"
                          aria-label="Open row actions"
                        >
                          <v-icon size="20">mdi-dots-vertical</v-icon>
                        </button>
                      </template>

                      <div class="table-action-menu">
                        <button
                          v-for="menuItem in getActionMenuItems(item)"
                          :key="menuItem.key"
                          type="button"
                          class="table-action-menu__item"
                          :class="{
                            'table-action-menu__item--danger':
                              menuItem.type === 'delete',
                          }"
                          :disabled="menuItem.disabled"
                          @click="triggerMenuAction(menuItem, item)"
                        >
                          <v-icon
                            v-if="menuItem.icon"
                            class="table-action-menu__icon"
                            size="24"
                          >
                            {{ menuItem.icon }}
                          </v-icon>
                          <img
                            v-else-if="menuItem.iconSrc"
                            class="table-action-menu__image"
                            :src="menuItem.iconSrc"
                            :alt="menuItem.label"
                          />
                          <span>{{ menuItem.label }}</span>
                        </button>
                      </div>
                    </v-menu>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Data Info Display -->
    <div v-if="showDataInfo && !loading" class="data-info-container">
      <div class="data-info-text">
        Showing <strong>{{ currentShowing }}</strong> of {{ totalRows }} entries
      </div>
    </div>

    <template v-if="items?.length && totalPages > 1">
      <BasePagination
        :total-pages="totalPages"
        :model-value="currentPage"
        @update:model-value="updatePage"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, defineEmits, computed, defineProps } from "vue";
import BasePagination from "./BasePagination.vue";
import SortIcon from "../icons/SortIcon.vue";

// Emit events for actions
const emit = defineEmits([
  "action",
  "sort",
  "update:current-page",
  "custom-action",
]);

const triggerAction = (type, item) => {
  emit("action", { type, item });
};

const triggerCustomAction = (type, item) => {
  emit("custom-action", { type, item });
};

// Define props
const props = defineProps({
  headers: {
    type: Array,
    required: true,
  },
  items: {
    type: Array,
    required: true,
  },
  isAction: {
    type: Boolean,
    default: false,
  },
  isViewAction: {
    type: Boolean,
    default: false,
  },
  isEditAction: {
    type: Boolean,
    default: false,
  },
  isDeleteAction: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  useApiSort: {
    type: Boolean,
    default: false,
  },
  totalPages: {
    type: Number,
    required: true,
  },
  currentPage: {
    type: Number,
    required: true,
  },
  customActions: {
    type: Array,
    default: () => [],
  },
  showDataInfo: {
    type: Boolean,
    default: false,
  },
  totalRows: {
    type: Number,
    default: 0,
  },
  currentLimit: {
    type: Number,
    default: 10,
  },
  variant: {
    type: String,
    default: "default",
    validator: (value) => ["default", "rounded"].includes(value),
  },
});

// Function to update page and emit event
const updatePage = (page) => {
  emit("update:current-page", page);
};

// Pagination and sorting states
const currentSort = ref({ column: "", order: "asc" });
const activeActionMenu = ref(null);

const STANDARD_ACTION_ICONS = {
  view: "mdi-eye-outline",
  edit: "mdi-pencil-outline",
  duplicate: "mdi-content-copy",
  delete: "mdi-trash-can-outline",
};

const STANDARD_ACTION_LABELS = {
  view: "View",
  edit: "Edit",
  duplicate: "Duplicate",
  delete: "Delete",
};

// Function to handle column sorting
const sortColumn = (column) => {
  if (currentSort.value.column === column) {
    currentSort.value.order =
      currentSort.value.order === "asc" ? "desc" : "asc";
  } else {
    currentSort.value.column = column;
    currentSort.value.order = "asc";
  }

  if (props.useApiSort) {
    emit("sort", {
      sort_by: column,
      sort_dir: currentSort.value.order.toUpperCase(),
    });
  }
};

// Computed property for sorted items
const sortedItems = computed(() => {
  // If no column is selected, return the original items
  if (!currentSort.value.column) return props.items;

  const sorted = [...props.items].sort((a, b) => {
    const valA = a[currentSort.value.column];
    const valB = b[currentSort.value.column];

    // Handle undefined or null values
    if (valA === null || valA === undefined) return 1;
    if (valB === null || valB === undefined) return -1;

    // Compare values
    if (valA < valB) return currentSort.value.order === "asc" ? -1 : 1;
    if (valA > valB) return currentSort.value.order === "asc" ? 1 : -1;
    return 0;
  });

  return sorted;
});

const getActionMenuKey = (item, itemIndex) => {
  return item?.id ?? itemIndex;
};

const setActiveActionMenu = (isOpen, item, itemIndex) => {
  activeActionMenu.value = isOpen ? getActionMenuKey(item, itemIndex) : null;
};

const formatActionLabel = (type) => {
  if (!type) return "";

  return type
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getCustomActionIcon = (action, item) => {
  if (!action.icon || typeof action.icon !== "function") {
    return action.icon;
  }

  return action.icon(item);
};

const createStandardAction = (type, item) => ({
  key: type,
  type,
  label: STANDARD_ACTION_LABELS[type],
  icon: STANDARD_ACTION_ICONS[type],
  disabled: Boolean(item.disableAction?.[type]),
  isCustom: false,
});

const getActionMenuItems = (item) => {
  const standardActions = [];

  if (props.isViewAction) {
    standardActions.push(createStandardAction("view", item));
  }

  if (props.isEditAction) {
    standardActions.push(createStandardAction("edit", item));
  }

  const customActionItems = props.customActions.map((action, actionIndex) => {
    const type = action.type;
    const iconSrc = getCustomActionIcon(action, item);

    return {
      key: `custom-${type}-${actionIndex}`,
      type,
      label:
        action.label ||
        action.text ||
        STANDARD_ACTION_LABELS[type] ||
        formatActionLabel(type),
      icon: STANDARD_ACTION_ICONS[type],
      iconSrc,
      disabled: Boolean(action.isDisabled && action.isDisabled(item)),
      isCustom: true,
    };
  });

  if (props.isDeleteAction) {
    standardActions.push(createStandardAction("delete", item));
  }

  return [
    ...standardActions.filter((action) => action.type !== "delete"),
    ...customActionItems,
    ...standardActions.filter((action) => action.type === "delete"),
  ];
};

const triggerMenuAction = (menuItem, item) => {
  if (menuItem.disabled) return;

  if (menuItem.isCustom) {
    triggerCustomAction(menuItem.type, item);
    return;
  }

  triggerAction(menuItem.type, item);
};

// Computed properties for data info display
const currentShowing = computed(() => props.items?.length || 0);
const totalRows = computed(() => props.totalRows || 0);

// Style functions for variant support
const isHeaderCentered = (header) =>
  Boolean(header?.centerColumn || header?.centerHeader);

const isBodyCentered = (header) =>
  Boolean(header?.centerColumn || header?.centerBody || header?.centerRow);

const getHeaderStyle = (index) => {
  const isRounded = props.variant === "rounded";
  const isFirst = index === 0;
  const isLast =
    index === props.headers.length - 1 &&
    !(props.isAction || props.customActions.length > 0);

  const baseStyle = {
    backgroundColor: "var(--primary-blue)",
    color: "#ffffff",
    fontWeight: "700",
    wordWrap: "break-word",
    cursor: props.headers[index].sortable ? "pointer" : "default",
  };

  if (isRounded) {
    return {
      ...baseStyle,
      fontSize: "20px",
      lineHeight: "28px",
      letterSpacing: "0.15px",
      textAlign: isHeaderCentered(props.headers[index]) ? "center" : "left",
      padding: "20px 10px",
      borderTop: "1px solid #bac1cc",
      borderBottom: "1px solid #bac1cc",
      borderLeft: isFirst ? "1px solid #bac1cc" : "none",
      borderRight: isLast ? "1px solid #bac1cc" : "none",
    };
  }

  return {
    ...baseStyle,
    fontSize: "var(--fs-medium)",
    textAlign: isHeaderCentered(props.headers[index]) ? "center" : "left",
    paddingTop: "15px",
    maxWidth: "80px",
    height: "50px",
  };
};

const getHeaderTextStyle = (header) => {
  if (props.variant === "rounded") {
    return {
      flex: isHeaderCentered(header) ? "0 1 auto" : "1 0 0",
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      textAlign: isHeaderCentered(header) ? "center" : "left",
    };
  }
  return {
    textAlign: isHeaderCentered(header) ? "center" : "left",
  };
};

const getActionHeaderStyle = () => {
  const isRounded = props.variant === "rounded";

  const baseStyle = {
    backgroundColor: "var(--primary-blue)",
    color: "#ffffff",
    fontWeight: "700",
    textAlign: "center",
    width: "10% !important",
    maxWidth: "min-content !important",
    whiteSpace: "nowrap",
  };

  if (isRounded) {
    return {
      ...baseStyle,
      fontSize: "20px",
      lineHeight: "28px",
      letterSpacing: "0.15px",
      padding: "20px 10px",
      borderTop: "1px solid #bac1cc",
      borderBottom: "1px solid #bac1cc",
      borderRight: "1px solid #bac1cc",
      borderLeft: "none",
    };
  }

  return {
    ...baseStyle,
    fontSize: "var(--fs-medium)",
    paddingTop: "15px",
    paddingLeft: "10px",
    paddingRight: "10px",
    border: "none",
    height: "50px",
  };
};

const getCellStyle = (index) => {
  const isRounded = props.variant === "rounded";
  const isFirst = index === 0;
  const isLast =
    index === props.headers.length - 1 &&
    !(props.isAction || props.customActions.length > 0);

  const baseStyle = {
    wordWrap: "break-word",
    overflowWrap: "break-word",
  };

  if (isRounded) {
    return {
      ...baseStyle,
      fontSize: "16px",
      fontWeight: "500",
      lineHeight: "24px",
      letterSpacing: "0.5px",
      textAlign: isBodyCentered(props.headers[index]) ? "center" : "left",
      padding: "20px 10px",
      height: "60px",
      borderBottom: "1px solid #bac1cc",
      borderLeft: isFirst ? "1px solid #bac1cc" : "none",
      borderRight: isLast ? "1px solid #bac1cc" : "none",
      color: "#3b4648",
    };
  }

  return {
    ...baseStyle,
    fontSize: "var(--fs-medium)",
    fontWeight: "400",
    textAlign: isBodyCentered(props.headers[index]) ? "center" : "left",
    padding: "10px",
    borderBottom: "1px solid #ddd",
    maxWidth: "50px",
  };
};

const getActionCellStyle = () => {
  const isRounded = props.variant === "rounded";

  const baseStyle = {
    width: "10% !important",
    maxWidth: "min-content !important",
    whiteSpace: "nowrap",
  };

  if (isRounded) {
    return {
      ...baseStyle,
      padding: "20px 10px",
      height: "60px",
      borderBottom: "1px solid #bac1cc",
      borderRight: "1px solid #bac1cc",
      borderLeft: "none",
    };
  }

  return {
    ...baseStyle,
    padding: "10px",
    borderBottom: "1px solid #ddd",
  };
};
</script>

<style scoped>
.skeleton-loader {
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.info-icon-container {
  position: relative;
  display: inline-block;
  margin-left: 5px;
}

.info-icon {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.tooltip {
  visibility: hidden;
  position: absolute;
  z-index: 1;
  width: 200px;
  background-color: #fff;
  color: #333;
  text-align: left;
  border-radius: 6px;
  padding: 10px;
  font-size: var(--fs-small);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 5px;
  opacity: 0;
  transition: opacity 0.3s;
}

.info-icon-container:hover .tooltip {
  visibility: visible;
  opacity: 1;
}

.tooltip::after {
  content: "";
  position: absolute;
  bottom: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent #fff transparent;
}

.data-info-container {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 10px 0;
  margin-top: 10px;
  border-top: 1px solid #e0e0e0;
}

.data-info-text {
  font-size: var(--fs-medium);
  color: #666;
  font-weight: 400;
}

.action-menu-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

.table-action-trigger {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  color: var(--primary-blue);
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease;
}

.table-action-trigger--active {
  color: var(--white);
  background: var(--blue-dark-2);
  border-color: var(--white);
}

.table-action-menu {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 162px;
  padding: 8px;
  background: var(--white);
  border-radius: 12px;
  box-shadow:
    0 2px 3px rgba(0, 0, 0, 0.1),
    0 6px 10px 4px rgba(0, 0, 0, 0.15);
}

.table-action-menu__item {
  display: flex;
  align-items: center;
  width: 100%;
  height: 48px;
  gap: 8px;
  padding: 10px 16px;
  color: var(--primary-blue);
  background: transparent;
  border: 0;
  border-radius: 12px;
  cursor: pointer;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0.15px;
  text-align: left;
  white-space: nowrap;
}

.table-action-menu__item:hover {
  background: var(--white-hover-1);
}

.table-action-menu__item:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.table-action-menu__item--danger {
  color: var(--error-red);
}

.table-action-menu__icon,
.table-action-menu__image {
  flex: 0 0 auto;
}

.table-action-menu__image {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

/* Rounded variant styles */
.rounded-header-first {
  border-top-left-radius: 16px;
}

.rounded-header-last {
  border-top-right-radius: 16px;
}

.rounded-cell-first-last-row {
  border-bottom-left-radius: 16px;
}

.rounded-cell-last-last-row {
  border-bottom-right-radius: 16px;
}
</style>
